import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  IconButton,
  Card,
  CardBody,
  FormErrorMessage,
  Skeleton,
  Text,
  Badge,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { BsArrowLeft, BsPlus, BsTrash } from "react-icons/bs";
import ReactSelect from "react-select";
import procurementApi from "../services/procurementApi";
import organizationalStructureApi from "../services/organizationalStructureApi";
import { employeeService } from "../../employee/employeeService";

const WorkflowEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    unit: "",
    department: "",
    branch: "",
  });
  const [stages, setStages] = useState([]);
  const [errors, setErrors] = useState({});

  // Business unit options from backend
  const [businessUnits, setBusinessUnits] = useState({
    units: [],
    departments: [],
    branches: [],
  });

  // Helper function to get employee display name
  const getEmployeeName = (emp) => {
    if (!emp) return "Unknown Employee";

    // Try different field name combinations
    const firstName =
      emp.firstName || emp.first_name || emp.name?.split(" ")[0] || "";
    const lastName =
      emp.lastName || emp.last_name || emp.name?.split(" ")[1] || "";
    const fullName = emp.fullName || emp.full_name || emp.name || "";

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    if (fullName) {
      return fullName;
    }

    return emp.email || emp.employeeId || "Unknown";
  };

  const employeeOptions = employees.map((emp) => ({
    value: emp._id,
    label: `${getEmployeeName(emp)}${emp.role || emp.ROLE ? ` (${emp.role || emp.ROLE})` : ""}`,
  }));

  useEffect(() => {
    fetchWorkflow();
    fetchEmployees();
    fetchBusinessUnits();
  }, [id]);

  const fetchBusinessUnits = async () => {
    try {
      const [units, departments, branches] = await Promise.all([
        organizationalStructureApi.getUnits({ isActive: true }),
        organizationalStructureApi.getDepartments({ isActive: true }),
        organizationalStructureApi.getBranches({ isActive: true }),
      ]);

      setBusinessUnits({
        units: (units || []).map((item) => item.name).filter(Boolean),
        departments: (departments || [])
          .map((item) => item.name)
          .filter(Boolean),
        branches: (branches || []).map((item) => item.name).filter(Boolean),
      });
    } catch (error) {
      console.error("Failed to load business units:", error);
      // Keep empty lists if master fetch fails
      setBusinessUnits({
        units: [],
        departments: [],
        branches: [],
      });
    }
  };

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const data = await employeeService.getAllEmployees();
      console.log("Fetched employees for workflow:", data);
      console.log("First employee structure:", data?.[0]);
      console.log("Employee count:", data?.length);
      setEmployees(data || []);
      if (!data || data.length === 0) {
        console.warn("No employees returned from API");
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
      toast({
        title: "Warning",
        description: "Could not load employees for approver selection",
        status: "warning",
        duration: 3000,
      });
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchWorkflow = async () => {
    try {
      setFetchLoading(true);
      const response = await procurementApi.getWorkflows();
      const workflow = (response?.data || response || []).find(
        (w) => w._id === id,
      );
      if (!workflow) {
        throw new Error("Workflow not found");
      }
      setFormData({
        name: workflow.name || "",
        description: workflow.description || "",
        unit: workflow.unit || "",
        department: workflow.department || "",
        branch: workflow.branch || "",
      });
      setStages(
        workflow.stages?.map((stage, index) => ({
          _id: stage._id,
          stageName: stage.stageName || "",
          approverType:
            stage.approverUsers?.length > 0
              ? "users"
              : (stage.approverType === "ROLE"
                  ? "role"
                  : stage.approverType?.toLowerCase()) || "role",
          approverRole: stage.approverRole || "",
          approverUsers: (stage.approverUsers || []).slice(0, 1),
          order:
            stage.stageNumber !== undefined
              ? stage.stageNumber + 1
              : stage.order || index + 1,
          isRequired: stage.isRequired !== undefined ? stage.isRequired : true,
          requireAllApprovers: stage.requireAllApprovers || false,
        })) || [],
      );
    } catch (error) {
      toast({
        title: "Error fetching workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const addStage = () => {
    setStages([
      ...stages,
      {
        stageName: "",
        approverType: "role",
        approverRole: "",
        approverUsers: [],
        order: stages.length + 1,
        requireAllApprovers: false,
        isRequired: true,
      },
    ]);
  };

  const updateStage = (index, field, value) => {
    setStages(
      stages.map((stage, i) =>
        i === index ? { ...stage, [field]: value } : stage,
      ),
    );
  };

  const removeStage = (index) => {
    setStages(stages.filter((_, i) => i !== index));
    // Reorder remaining stages
    const reorderedStages = stages
      .filter((_, i) => i !== index)
      .map((stage, i) => ({ ...stage, order: i + 1 }));
    setStages(reorderedStages);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Workflow name must be at least 3 characters";
    }

    if (!formData.unit || formData.unit.trim().length === 0) {
      newErrors.unit = "Unit is required";
    }

    if (!formData.department || formData.department.trim().length === 0) {
      newErrors.department = "Department is required";
    }

    if (!formData.branch || formData.branch.trim().length === 0) {
      newErrors.branch = "Branch is required";
    }

    if (stages.length === 0) {
      newErrors.stages = "At least one stage is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one approval stage",
        status: "error",
        duration: 3000,
      });
    }

    const invalidStages = stages.filter(
      (stage) =>
        !stage.stageName ||
        (stage.approverType === "role" && !stage.approverRole) ||
        (stage.approverType === "users" && !stage.approverUsers?.[0]),
    );
    if (invalidStages.length > 0) {
      newErrors.stages = "All stages must have name and approver";
      toast({
        title: "Validation Error",
        description: "Please complete all stage fields (name and approver)",
        status: "error",
        duration: 3000,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const workflowData = {
        name: formData.name,
        description: formData.description,
        unit: formData.unit,
        department: formData.department,
        branch: formData.branch,
        stages: stages.map((stage) => {
          const stageData = {
            stageName: stage.stageName,
            approverType: stage.approverType === "users" ? "USER" : "ROLE",
            requireAllApprovers: stage.requireAllApprovers || false,
          };

          // Only include approverRole for role-based approvers
          if (stage.approverType === "role") {
            stageData.approverRole = stage.approverRole;
            stageData.approverUsers = [];
          } else {
            // Only include approverUsers for user-based approvers
            stageData.approverUsers = (stage.approverUsers || []).slice(0, 1);
            stageData.approverRole = "";
          }

          return stageData;
        }),
      };
      await procurementApi.updateWorkflow(id, workflowData);
      toast({
        title: "Workflow updated successfully",
        status: "success",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } catch (error) {
      toast({
        title: "Error updating workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/procurement/workflows");
  };

  if (fetchLoading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack mb={6}>
        <Button
          leftIcon={<BsArrowLeft />}
          variant="ghost"
          onClick={handleCancel}
        >
          Back
        </Button>
        <Heading size="lg">Edit Workflow</Heading>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Workflow Details */}
            <FormControl isInvalid={errors.name} isRequired>
              <FormLabel>Workflow Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Standard Procurement Approval"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe this workflow"
                rows={3}
              />
            </FormControl>

            {/* Workflow Routing Fields */}
            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              borderWidth="1px"
              borderColor="blue.200"
            >
              <Text fontSize="sm" fontWeight="semibold" color="blue.700" mb={3}>
                Workflow Auto-Selection Criteria
              </Text>
              <Text fontSize="xs" color="blue.600" mb={4}>
                This workflow will be automatically assigned to procurement
                requests matching the unit, department, and branch combination
                below.
              </Text>
              <VStack spacing={4} align="stretch">
                <FormControl isInvalid={errors.unit} isRequired>
                  <FormLabel fontSize="sm">Unit</FormLabel>
                  <Select
                    value={formData.unit}
                    onChange={(e) => handleInputChange("unit", e.target.value)}
                    placeholder="Select unit"
                    bg="white"
                  >
                    {businessUnits.units.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.unit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.department} isRequired>
                  <FormLabel fontSize="sm">Department</FormLabel>
                  <Select
                    value={formData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                    placeholder="Select department"
                    bg="white"
                  >
                    {businessUnits.departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.department}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.branch} isRequired>
                  <FormLabel fontSize="sm">Branch</FormLabel>
                  <Select
                    value={formData.branch}
                    onChange={(e) =>
                      handleInputChange("branch", e.target.value)
                    }
                    placeholder="Select branch"
                    bg="white"
                  >
                    {businessUnits.branches.map((branch) => (
                      <option key={branch} value={branch}>
                        {branch}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.branch}</FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>

            {/* Approval Stages */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="sm">Approval Stages</Heading>
                <Button
                  leftIcon={<BsPlus />}
                  size="sm"
                  onClick={addStage}
                  variant="outline"
                  colorScheme="blue"
                >
                  Add Stage
                </Button>
              </HStack>

              <VStack spacing={4} align="stretch">
                {stages.map((stage, index) => (
                  <Card key={stage._id || `new-stage-${index}`}>
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        <HStack align="start" spacing={4}>
                          <FormControl flex={1} isRequired>
                            <FormLabel fontSize="sm">Stage Name</FormLabel>
                            <Input
                              size="sm"
                              value={stage.stageName}
                              onChange={(e) =>
                                updateStage(index, "stageName", e.target.value)
                              }
                              placeholder="e.g., Manager Approval"
                            />
                          </FormControl>

                          <FormControl maxW="150px">
                            <FormLabel fontSize="sm">Approver Type</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverType || "role"}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "approverType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="role">By Role</option>
                              <option value="users">Specific Users</option>
                            </Select>
                          </FormControl>

                          <FormControl maxW="100px">
                            <FormLabel fontSize="sm">Order</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={stage.order}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "order",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                            />
                          </FormControl>

                          <IconButton
                            mt={7}
                            size="sm"
                            icon={<BsTrash />}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeStage(index)}
                            aria-label="Remove stage"
                            isDisabled={stages.length === 1}
                          />
                        </HStack>

                        {/* Approver Role Selection */}
                        {(stage.approverType === "role" ||
                          !stage.approverType) && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Approver Role</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverRole}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "approverRole",
                                  e.target.value,
                                )
                              }
                              placeholder="Select role"
                            >
                              <option value="SuperAdmin">Super Admin</option>
                              <option value="Admin">Admin</option>
                              <option value="HR">HR</option>
                              <option value="Manager">Manager</option>
                              <option value="Employee">Employee</option>
                            </Select>
                          </FormControl>
                        )}

                        {/* Approver Users Selection */}
                        {stage.approverType === "users" && (
                          <FormControl isRequired>
                            <HStack justify="space-between" align="center">
                              <FormLabel fontSize="sm" mb={0}>
                                Select Approver User*
                                {stage.approverUsers?.length > 0 && (
                                  <Badge ml={2} colorScheme="blue">
                                    1 selected
                                  </Badge>
                                )}
                                {!employeesLoading && employees.length > 0 && (
                                  <Badge
                                    ml={2}
                                    colorScheme="green"
                                    variant="subtle"
                                  >
                                    {employees.length} available
                                  </Badge>
                                )}
                              </FormLabel>
                              {!employeesLoading && employees.length === 0 && (
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="outline"
                                  onClick={fetchEmployees}
                                >
                                  Retry
                                </Button>
                              )}
                            </HStack>
                            {employeesLoading ? (
                              <Box mt={2}>
                                <Skeleton height="40px" />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  Loading employees...
                                </Text>
                              </Box>
                            ) : employees.length === 0 ? (
                              <Box mt={2}>
                                <Text
                                  fontSize="sm"
                                  color="orange.600"
                                  fontWeight="medium"
                                >
                                  No employees found.
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                  Please add employees first or check your
                                  permissions.
                                </Text>
                              </Box>
                            ) : (
                              <VStack spacing={2} align="stretch">
                                <Box>
                                  <ReactSelect
                                    placeholder="Search and select an approver"
                                    isClearable
                                    options={employeeOptions}
                                    value={
                                      employeeOptions.find(
                                        (option) =>
                                          option.value ===
                                          stage.approverUsers?.[0],
                                      ) || null
                                    }
                                    onChange={(selectedOption) =>
                                      updateStage(
                                        index,
                                        "approverUsers",
                                        selectedOption
                                          ? [selectedOption.value]
                                          : [],
                                      )
                                    }
                                  />
                                </Box>
                                <Text fontSize="xs" color="gray.500">
                                  One stage can have only one specific user
                                  approver.
                                </Text>
                              </VStack>
                            )}
                          </FormControl>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </Box>

            {/* Master data empty guard */}
            {!loading &&
              (businessUnits.units.length === 0 ||
                businessUnits.departments.length === 0 ||
                businessUnits.branches.length === 0) && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Some master data is missing. Please add{" "}
                    {businessUnits.units.length === 0 ? "Units" : ""}
                    {businessUnits.units.length === 0 &&
                    businessUnits.departments.length === 0
                      ? ", "
                      : ""}
                    {businessUnits.departments.length === 0
                      ? "Departments"
                      : ""}
                    {(businessUnits.units.length === 0 ||
                      businessUnits.departments.length === 0) &&
                    businessUnits.branches.length === 0
                      ? ", "
                      : ""}
                    {businessUnits.branches.length === 0 ? "Branches" : ""} in{" "}
                    <strong>Organizational Structure Maintenance</strong> first.
                  </Text>
                </Alert>
              )}

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end" pt={4}>
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                isDisabled={
                  businessUnits.units.length === 0 ||
                  businessUnits.departments.length === 0 ||
                  businessUnits.branches.length === 0
                }
                title={
                  businessUnits.units.length === 0 ||
                  businessUnits.departments.length === 0 ||
                  businessUnits.branches.length === 0
                    ? "Please populate master data before saving"
                    : undefined
                }
              >
                Update Workflow
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default WorkflowEdit;
